/** A card's repair by the resolver, made on a resolver attached to the plan (see planFixes). */
import {
  fragmentsSatisfied,
  groupsInOrder,
  gradientResidue,
  intrudersCleared,
  relocateIntruders,
  rejoinFragments,
  relocateOrderOutliers,
  resequenceFallback,
  sizeOrderSatisfied,
  type ResolverStep,
  type ResolverStudio,
} from "./cdtResolve";
import type { ResolverInputs } from "./toResolverInputs";

type Pin = NonNullable<ResolverInputs["pinByFlagId"][string]>;

/** Is the pin's block as the pin asks, on the resolver's plan as it stands? */
const satisfiesPinOn = (studio: ResolverStudio, pin: Pin, flagKind?: string): boolean => {
  try {
    if (pin.fragment) return fragmentsSatisfied(studio, pin);
    if (pin.seqOrder && pin.seqOrder.length > 1) {
      if (pin.kind === "size-order") return sizeOrderSatisfied(studio, pin);
      return studio.check(pin).resolved && groupsInOrder(studio, pin);
    }
    // Flag kind discriminates; vendored check is depth-2.
    if (flagKind === "contiguity") {
      return studio.check(pin).resolved && intrudersCleared(studio, pin);
    }
    return studio.check(pin).resolved;
  } catch {
    return false;
  }
};

const resolveFragmentPin = (studio: ResolverStudio, pin: Pin): ResolverStep => {
  const step = rejoinFragments(studio, pin);
  if (step) return { ...step, resolved: fragmentsSatisfied(studio, pin) };
  return {
    pin: pin.scope,
    kind: pin.kind,
    action: "none",
    ok: true,
    resolved: true,
    alreadyClean: true,
    physicalChange: false,
    notes: [],
    diff: { introduced: 0 },
  };
};

/**
 * One card's repair, made on the resolver's plan: the moves it takes to put the pin's block
 * as the pin asks. The caller reads the plan it left from `studio.positions()`.
 */
export const resolvePinOn = (studio: ResolverStudio, pin: Pin, flagKind: string): ResolverStep => {
  if (pin.fragment) return resolveFragmentPin(studio, pin);
  const satisfiesPin = (target: Pin) => satisfiesPinOn(studio, target);
  const rejoinNotes: string[] = [];
  let rejoined = false;
  if (pin.seqOrder && pin.seqOrder.length > 1) {
    const rejoin = rejoinFragments(studio, pin);
    if (rejoin) {
      rejoined = rejoin.physicalChange === true;
      rejoinNotes.push(...rejoin.notes, ...(rejoin.reason ? [rejoin.reason] : []));
    }
  }
  let step = studio.resolve(pin);
  // Vendored check calls intruders already satisfied.
  if (flagKind === "contiguity" && !intrudersCleared(studio, pin)) {
    const rotation = relocateIntruders(studio, pin);
    if (rotation) {
      step = {
        ...step,
        ...rotation,
        notes: [...step.notes, ...rotation.notes],
        alreadyClean: false,
      };
    }
  }
  if (pin.seqOrder && pin.seqOrder.length > 1) {
    const residue = rejoinFragments(studio, pin);
    if (residue?.physicalChange) {
      rejoined = true;
      rejoinNotes.push(...residue.notes);
    }
  }
  // Rejoin can settle the sequence; "already clean" would misread.
  if (rejoined && step.alreadyClean) {
    step = {
      ...step,
      alreadyClean: false,
      physicalChange: true,
      action: "relocate",
      reason: undefined,
    };
  }
  if (rejoinNotes.length) step = { ...step, notes: [...rejoinNotes, ...step.notes] };
  if (pin.seqOrder && pin.seqOrder.length > 1) {
    const outliers = relocateOrderOutliers(studio, pin);
    if (outliers?.physicalChange) {
      rejoinNotes.push(...outliers.notes);
      rejoined = true;
      step = { ...step, resolved: satisfiesPin(pin), alreadyClean: false };
    }
  }
  if (pin.seqOrder && pin.seqOrder.length > 1 && !step.resolved && !satisfiesPin(pin)) {
    if (!step.resolved && !groupsInOrder(studio, pin)) {
      const fallback = resequenceFallback(studio, pin);
      if (fallback.physicalChange || step.alreadyClean) {
        step = { ...fallback, resolved: satisfiesPin(pin) };
      }
    }
    if (!step.resolved) {
      const alongClean = groupsInOrder(studio, pin);
      const residue = gradientResidue(studio, pin);
      const gradeNote = step.notes.find((note) => /gradient|re-hang/i.test(note));
      const parts: string[] = [];
      parts.push(
        alongClean ? "each shelf is now in order" : "a stray piece has no room to rejoin the block",
      );
      if (pin.kind === "size-order" && residue > 0) {
        parts.push(
          `${residue} size pair(s) still reversed across shelves — ` +
            (gradeNote ?? "moving them would put more big packs above small"),
        );
      }
      step = { ...step, partial: true, reason: parts.join("; ") };
    }
  }
  return step;
};
